import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import TableCellWrapper from './TableCellWrapper.js'

import {
  getConstraintErrorIdForColumn,
  getFieldId,
  getLegacySubfieldId,
  getReviewId,
  getCommentId,
  getTableReviewId,
  getTableCommentId,
  getFieldIdFromKey,
} from '../../../utils/utils.js'

import {
  usePreviousAllReviewData,
  usePreviousProcessData,
  useProcessContext,
  useSelectedTableData,
  useFieldValidationError,
} from '../../../../../store.js'

import { ACTIVITY_TYPES, FIELD_TYPES } from '../../../../../utility/constants.js'

import { autoComputeFieldValueOptimized } from '../../../utils/autoComputeOptimized.js'
import { useAuthContext } from '../../../../../hooks/useAuthContext.js'
import { getDefaultValueForField } from '../../../utils/fieldUtil.js'
import { toast } from 'react-toastify'

const RowWiseDataEntryModal = ({
  open,
  onClose,
  index,
  row,
  currentRowData,
  columns = [],
  hiddenColumnIds = [],
  currentReviewDataRow,
  currentCommentDataRow,
  isImported,
  allValues,
  isFieldDisabled = () => false,
  isHidden = () => false,
  syncWithParent = () => {},
  onSubmit = async () => {},
  title = 'Edit Row',
}) => {
  const { user } = useAuthContext()
  const processContext = useProcessContext((state) => state.processContext)
  const selectedTableData = useSelectedTableData((state) => state.selectedTableData)
  const fieldValidationError = useFieldValidationError((state) => state.fieldValidationError)

  const listDataIdMetadataMap = useProcessContext(
    (state) => state.processContext.listDataIdMetadataMap,
  )

  const activityType = processContext?.activityTemplate?.activity_type
  const version = processContext?.processData?.version

  /**
   * ============================================================
   * TEMPORARY MODAL STATE
   * ============================================================
   *
   * This is the ONLY row state the modal modifies.
   *
   * currentRowData is never modified directly.
   */
  const [modalRowData, setModalRowData] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Source of truth for the currently edited row.
  // Unlike React state, this is always immediately updated.
  const modalRowDataRef = useRef(null)

  // Used to invalidate older async computations.
  const changeVersionRef = useRef(0)

  // Tracks which row the modal was initialized for.
  const initializedRowRef = useRef(null)

  /**
   * ============================================================
   * FIELD MAP
   * ============================================================
   */

  const idFieldMap = useMemo(() => {
    const map = {}

    processContext?.allFields?.forEach((field) => {
      map[field.id] = field
    })

    return map
  }, [processContext?.allFields])

  /**
   * ============================================================
   * COMPUTED FIELDS
   * ============================================================
   */

  const computableFields = useMemo(() => columns.filter((field) => field.is_computed), [columns])

  /**
   * ============================================================
   * CONTEXT
   * ============================================================
   */

  const computeContext = useMemo(
    () => ({
      processPrefix: processContext?.processTemplate?.prefix,
      processReferenceNum: processContext?.processData?.process_reference_num,
      processTemplateId: processContext?.processData?.process_template_id,
      version: processContext?.processData?.version,
    }),
    [processContext],
  )

  /**
   * ============================================================
   * OPEN / RESET
   * ============================================================
   *
   * Every time modal opens, create a fresh copy.
   */

  useEffect(() => {
    if (!open || !currentRowData) {
      return
    }

    const rowKey = `${index}-${row}`

    // Don't reinitialize an already-open modal just because
    // currentRowData received a new object reference.
    if (initializedRowRef.current === rowKey) {
      return
    }

    const initialRowData = {
      ...currentRowData,
    }

    initializedRowRef.current = rowKey
    changeVersionRef.current += 1

    modalRowDataRef.current = initialRowData

    setModalRowData(initialRowData)
    setValidationErrors({})
  }, [open, currentRowData, index, row])

  /**
   * ============================================================
   * CLOSE
   * ============================================================
   */

  useEffect(() => {
    if (open) {
      return
    }

    initializedRowRef.current = null
    modalRowDataRef.current = null
    changeVersionRef.current += 1
  }, [open])

  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return
    }

    /**
     * Invalidate any currently running computation.
     */
    changeVersionRef.current += 1

    /**
     * Clear the authoritative modal row.
     */
    modalRowDataRef.current = null

    initializedRowRef.current = null

    setModalRowData(null)
    setValidationErrors({})

    onClose()
  }, [isSubmitting, onClose])

  /**
   * ============================================================
   * PREVIOUS PROCESS DATA
   * ============================================================
   */

  const previousReviewProcessDataValues = usePreviousProcessData((state) =>
    state.previousProcessData.prevReviewProcessData
      ? state.previousProcessData.prevReviewProcessData
      : {},
  )

  /**
   * ============================================================
   * PREVIOUS REVIEW DATA OF CURRENT ROW
   * ============================================================
   */

  const previousReviewDataOfCurrentRow = useMemo(() => {
    if (!columns?.length) {
      return {}
    }

    if (Object.keys(previousReviewProcessDataValues).length === 0) {
      return {}
    }

    return columns.reduce((acc, column) => {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,

          [getReviewId(column.id)]:
            previousReviewProcessDataValues.review_data?.[getTableReviewId(column.id, row)],
        }
      }

      return acc
    }, {})
  }, [columns, previousReviewProcessDataValues, row])

  /**
   * ============================================================
   * PREVIOUS REVIEW COMMENTS
   * ============================================================
   */

  const previousReviewCommentsDataOfCurrentRow = useMemo(() => {
    if (!columns?.length) {
      return {}
    }

    if (Object.keys(previousReviewProcessDataValues).length === 0) {
      return {}
    }

    return columns.reduce((acc, column) => {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,

          [getCommentId(column.id)]:
            previousReviewProcessDataValues.review_comments_data?.[
              getTableCommentId(column.id, row)
            ],
        }
      }

      return acc
    }, {})
  }, [columns, previousReviewProcessDataValues, row])

  /**
   * ============================================================
   * PREVIOUS ALL REVIEWS
   * ============================================================
   */

  const previousAllReviews = usePreviousAllReviewData((state) => state.previousAllReviews)

  const previousAllReviewsOfCurrentRow = useMemo(() => {
    if (!columns?.length) {
      return {}
    }

    if (!Object.keys(previousAllReviews).length) {
      return {}
    }

    return columns.reduce((acc, column) => {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,

          [getReviewId(column.id)]: previousAllReviews[getTableReviewId(column.id, row)],
        }
      }

      return acc
    }, {})
  }, [columns, previousAllReviews, row])

  /**
   * ============================================================
   * VISIBLE FIELDS
   * ============================================================
   */

  const modalColumns = useMemo(
    () =>
      columns
        .filter((field) => !hiddenColumnIds?.includes(field.id))
        .filter((field) => !isHidden(field, row)),
    [columns, hiddenColumnIds, isHidden, row],
  )

  const modalFieldDisabledMap = useMemo(() => {
    if (!modalRowData) {
      return {}
    }

    return modalColumns.reduce((acc, field) => {
      const disabled = isFieldDisabled(
        field,
        row,
        false,
        isImported,
        previousReviewDataOfCurrentRow,
        currentReviewDataRow,
        modalRowData,
      )

      acc[field.id] = disabled

      return acc
    }, {})
  }, [
    modalRowData,
    modalColumns,
    row,
    isImported,
    previousReviewDataOfCurrentRow,
    currentReviewDataRow,
    isFieldDisabled,
  ])

  /**
   * ============================================================
   * FIELD CHANGE
   * ============================================================
   *
   * IMPORTANT:
   *
   * We update modalRowData first.
   *
   * Then computed fields are calculated using that updated
   * temporary row.
   */

  const handleFieldChange = useCallback(
    async (id, value) => {
      if (!modalRowDataRef.current) {
        return
      }

      const field = idFieldMap[id]

      let normalizedValue = value

      if ([FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY].includes(field?.data_structure)) {
        normalizedValue = value === '' || value === null ? value : Number(value)
      }

      /**
       * Every field change gets a unique version.
       *
       * If another field changes before this computation finishes,
       * this computation becomes stale and its result is ignored.
       */
      const changeVersion = ++changeVersionRef.current

      /**
       * IMPORTANT:
       *
       * Do NOT use modalRowData here.
       *
       * modalRowData is the value captured by the current React render.
       *
       * modalRowDataRef.current is always the latest row.
       */
      const nextRowData = {
        ...modalRowDataRef.current,
        [getFieldId(id)]: normalizedValue,
      }

      /**
       * Update the authoritative row immediately.
       */
      modalRowDataRef.current = nextRowData

      /**
       * Update UI.
       */
      setModalRowData(nextRowData)

      /**
       * Compute dependent/computed fields using exactly the row
       * that caused this change.
       */
      const computedRowValues = await autoComputeFieldValueOptimized({
        allActivitiesCreatedAtWithVersion: processContext?.allActivitiesCreatedAtWithVersion,

        allProcessData: processContext?.allProcessData,

        context: computeContext,

        dynamicListData: processContext?.dynamicListData,

        holidayList: processContext?.holidayList,

        listDataIdMetadataMap,

        selectedTableData,

        tableRowIndex: index,

        tableSubfieldsMap: processContext?.tableSubfieldsMap,

        user,

        computeFields: computableFields,

        data: {
          allValues,
          rowValues: nextRowData,
        },

        idFieldMap,
      })

      /**
       * Another field was changed while computation was running.
       *
       * Therefore this result was calculated against an old row.
       *
       * NEVER apply it.
       */
      if (changeVersion !== changeVersionRef.current) {
        return
      }

      /**
       * Merge computed values into the CURRENT row,
       * not the row that existed when this handler started.
       */
      const finalRowData = {
        ...(modalRowDataRef.current || {}),
        ...computedRowValues,
      }

      /**
       * Keep ref and React state synchronized.
       */
      modalRowDataRef.current = finalRowData

      setModalRowData(finalRowData)
    },
    [
      idFieldMap,
      processContext,
      computeContext,
      listDataIdMetadataMap,
      selectedTableData,
      index,
      user,
      computableFields,
      allValues,
    ],
  )

  /**
   * ============================================================
   * VALIDATION
   * ============================================================
   *
   * Keep your existing validation here.
   */
  const validateRow = useCallback(
    async (modalRowData) => {
      for (const key in modalRowData) {
        const fieldId = getFieldIdFromKey(key)?.id
        const field = idFieldMap[fieldId]

        if (!field) {
          continue
        }

        const value = modalRowData[key]
        const disabled = modalFieldDisabledMap[field.id]

        // Disabled fields should not participate in validation
        if (disabled) {
          continue
        }

        // Required field must have a value
        if (
          field.is_required &&
          (value === undefined ||
            value === null ||
            value === '' ||
            value === getDefaultValueForField(field))
        ) {
          return false
        }
      }

      return true
    },
    [idFieldMap, modalFieldDisabledMap],
  )

  /**
   * ============================================================
   * SUBMIT
   * ============================================================
   */

  const handleSubmit = useCallback(async () => {
    if (!modalRowDataRef.current || isSubmitting) {
      return
    }

    const rowDataToSubmit = modalRowDataRef.current

    const isValid = await validateRow(rowDataToSubmit)

    if (!isValid) {
      toast.error('Enter all required fields with (*) marks...')
      return
    }

    try {
      setIsSubmitting(true)

      await onSubmit(rowDataToSubmit)

      /**
       * Invalidate any pending computation.
       */
      changeVersionRef.current += 1

      modalRowDataRef.current = null
      initializedRowRef.current = null

      setModalRowData(null)
      setValidationErrors({})

      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [isSubmitting, validateRow, onSubmit, onClose])

  /**
   * ============================================================
   * REVIEW SWITCH
   * ============================================================
   */

  const isReviewSwitchHidden = useCallback(
    (field) => {
      return (activityType === ACTIVITY_TYPES.DATA_ENTRY && version === 1) || field.is_read_only
    },
    [activityType, version],
  )

  /**
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <span style={{ fontSize: 14 }}>{`${title} | Row ${index + 1}`}</span>
      </DialogTitle>

      <DialogContent dividers>
        {modalRowData &&
          modalColumns.map((field) => (
            <Box
              key={getLegacySubfieldId(field.id, row)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: 12,
                }}
              >
                {field.label || field.field_template_name || field.name}{' '}
                {field.is_required ? '*' : ''}
              </Typography>

              <Box
                style={{
                  border:
                    field.data_structure == FIELD_TYPES.FILE &&
                    modalRowData[getFieldId(field.id)] != ''
                      ? '1px solid #c7c7c7'
                      : '',
                  borderRadius: '4px',
                  paddingRight: '8px',
                  marginRight:
                    field.data_structure == FIELD_TYPES.FILE &&
                    modalRowData[getFieldId(field.id)] != ''
                      ? '8px'
                      : '',
                }}
              >
                <TableCellWrapper
                  field={field}
                  rowId={row}
                  isSelectable={false}
                  isSelected={false}
                  isImported={isImported}
                  rowData={modalRowData}
                  rowReview={currentReviewDataRow}
                  rowComment={currentCommentDataRow}
                  previousReviewDataOfCurrentRow={previousReviewDataOfCurrentRow}
                  previousReviewCommentsDataOfCurrentRow={previousReviewCommentsDataOfCurrentRow}
                  previousAllReviewsOfCurrentRow={previousAllReviewsOfCurrentRow}
                  onChangeValue={handleFieldChange}
                  columnWidth="100%"
                  helperText={
                    validationErrors[field.id] ||
                    fieldValidationError[getConstraintErrorIdForColumn(field.id, row)]
                  }
                  isFieldDisabled={() => modalFieldDisabledMap[field.id]}
                  syncWithParent={syncWithParent}
                  allValues={allValues}
                  isReviewSwitchHidden={isReviewSwitchHidden}
                  isFrozen={false}
                  leftOffset={0}
                  isDataEntryModal={true}
                  fieldDisabled={modalFieldDisabledMap[field.id]}
                  requireFullRowEntry={true}
                />
              </Box>
            </Box>
          ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit} disabled={!modalRowData || isSubmitting}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RowWiseDataEntryModal
